import { ApolloClient } from '@apollo/client';
import { Auth } from 'aws-amplify';
import React, { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import { createClient } from '../graphql';
import AuthenticatedStatus from '../hooks/isUserAuthenticated';
import i18n from '../locale';
import { logErrorToMonitorSystem } from '../utils/errorLogging';

type Nullable<T> = T | null;

export interface AuthContextProps {
  loading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  wasLoggedIn?: boolean;
  client?: ApolloClient<any>;
}

interface AuthProviderProps {
  children: ReactNode;
}

/* eslint-disable */
const defaultProps: AuthContextProps = {
  loading: null,
  isAuthenticated: null,
  logout: null,
  wasLoggedIn: null,
  client: null,
};
/* eslint-enable */

const AuthContext = createContext<AuthContextProps>(defaultProps);

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);

  const [wasLoggedIn, setWasLoggedIn] = useState(false);
  const isAuthenticated = AuthenticatedStatus();
  const [apolloClient, setApolloClient] = useState<Nullable<ApolloClient<any>>>(null);

  const showError = (error: string) => {
    toast.error(error);
  };

  const errorCodesToIgnore = ['UNAUTHENTICATED'];
  const handledErrorCodes = ['BAD_USER_INPUT'];

  const handleError = (error: string, code?: string) => {
    // don't show a toast for UNAUTHENTICATED errors
    if (errorCodesToIgnore.includes(code || '')) return;
    if (handledErrorCodes.includes(code || '')) {
      showError(error);
      return logErrorToMonitorSystem(error);
    }

    logErrorToMonitorSystem(error);
    showError(i18n.t('common.genericError'));
  };

  const getClient = () => {
    setApolloClient(createClient(handleError, isAuthenticated));
  };

  useEffect(() => {
    void getClient();
    if (isAuthenticated !== null) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const logout = async () => {
    try {
      await Promise.allSettled([apolloClient.clearStore(), Auth.signOut()]);
    } catch (err: any) {
      logErrorToMonitorSystem(err);
    }
    localStorage.clear();
    setWasLoggedIn(true);
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        isAuthenticated,
        wasLoggedIn,
        logout,
        client: apolloClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext: () => AuthContextProps = () => {
  return React.useContext(AuthContext);
};
